import React from "react";
import { fileUrl } from "@/lib/api";
import { rupiah, fmtDate } from "@/lib/format";
import { CAT, LANTAI_CAT, MEK2_TIERS, calcItem, roomTot, calcLantai, lantaiTot, costVal, calcItemAllClasses } from "@/lib/kalkulatorCatalog";

// kelas badge colors for the printed/PDF kalkulator sheet — deliberately NOT the builder's
// bColors (those are light, high-opacity-on-dark-background colors tuned for the app's own
// dark theme; on a plain white printed page several of them fall below readable contrast).
// Pastel background + a fully saturated dark text of the same hue, so it stays legible
// whether or not the browser prints background colors at all.
const PRINT_BADGE = {
  std: { text: "#2A6010", bg: "#E8F5DC" },
  dlx: { text: "#0A4A8A", bg: "#E0EEFA" },
  lux: { text: "#7A4800", bg: "#FDF0D8" },
  oth: { text: "#3A2BA0", bg: "#ECEAFE" },
};

const HARI_ID = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const BULAN_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const idDateParts = (iso) => {
  if (!iso) return { hari: "-", tanggal: "-", bulan: "-", tahun: "-" };
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return { hari: "-", tanggal: "-", bulan: "-", tahun: "-" };
  return { hari: HARI_ID[dt.getDay()], tanggal: dt.getDate(), bulan: BULAN_ID[dt.getMonth()], tahun: dt.getFullYear() };
};

// Renders the actual start–end date span for a structured duration (days + start date),
// e.g. "1 - 30 Juni 2026", instead of a bare day count — so the printed contract always
// carries real dates a reader can check against the calendar.
const fmtDurationRange = (startIso, days) => {
  if (!startIso || !days) return null;
  const start = new Date(startIso + "T00:00:00");
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start);
  end.setDate(end.getDate() + Number(days) - 1);
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  return sameMonth
    ? `${start.getDate()} - ${end.getDate()} ${BULAN_ID[end.getMonth()]} ${end.getFullYear()}`
    : `${start.getDate()} ${BULAN_ID[start.getMonth()]} ${start.getFullYear()} - ${end.getDate()} ${BULAN_ID[end.getMonth()]} ${end.getFullYear()}`;
};

// Shifts an ISO date forward by n days — derives the physical-work start date from
// (Masa Persiapan start date + Masa Persiapan length), mirroring DocumentDialog.jsx's copy.
const addDaysIso = (iso, n) => {
  if (!iso) return iso;
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() + Number(n || 0));
  return d.toISOString().slice(0, 10);
};

function Wordmark({ company, maxHeight = 70 }) {
  if (company?.logo_url) return <img src={fileUrl(company.logo_url)} alt="logo" style={{ maxHeight, maxWidth: 180, objectFit: "contain" }} />;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 34, height: 34, border: "3px solid #1a1a1a", borderRight: "none", borderBottom: "none" }} />
      <div>
        <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: 6, lineHeight: 1, color: "#1a1a1a" }}>{company?.name || "ADARI"}</div>
        <div style={{ fontSize: 7, letterSpacing: 3, color: "#6b6b6b", marginTop: 2 }}>{company?.tagline}</div>
      </div>
    </div>
  );
}

// The actual SPH/Invoice sheet — pulled out of DocumentPrint so it can be reused both
// for printing a real saved document (fetched by doc_id) AND for previewing a pending
// document change's raw new_data before it's ever approved/saved (Persetujuan page).
// Takes only plain data as props, no fetching of its own.
export default function DocumentPreviewContent({ d, tpl }) {
  const isQuo = d.type === "quotation";
  const displayStatus = !isQuo && d.payment_status_actual ? d.payment_status_actual : d.payment?.status;
  const sisaTagihan = !isQuo ? (d.sisa_tagihan_actual ?? 0) : null;
  const items = d.items || [];
  const fillers = Math.max(0, 3 - items.length);
  const bd = "1px solid #cfcfcf";
  const cell = { border: bd, padding: "6px 8px", fontSize: 11 };
  const th = { ...cell, background: "#f3f3f3", fontWeight: 700, textTransform: "uppercase", fontSize: 10 };
  const branch = d.branch_address || {};
  const hasBranch = !!(branch.label || branch.address);
  const banks = d.bank_accounts_selected || [];
  const sig = d.signature_selected || {};
  const sigImg = sig.image_path || d.signature_url;
  const companyName = d.company?.name || "ADARI";
  const totalVolume = items.reduce((a, it) => a + Number(it.volume || 0), 0);
  const p = { fontSize: 11, lineHeight: 1.6, margin: "0 0 10px" };
  const pq = { fontSize: 10.5, lineHeight: 1.65, margin: "0 0 9px" };

  const Header = ({ compact }) => (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start", paddingBottom: compact ? 18 : 12 }}>
      <div style={{ flexShrink: 0 }}><Wordmark company={d.company} maxHeight={compact ? 52 : 70} /></div>
      <div style={{ flex: 1, textAlign: "right" }}>
        <div style={{ fontWeight: 700, fontSize: 12 }}>{d.company?.subtitle}</div>
        <div style={{ fontSize: 10, color: "#555", marginTop: 3 }}>{d.company?.address}</div>
        <div style={{ fontSize: 10, color: "#555" }}>Email : {d.company?.email} &nbsp; Telp : {d.company?.phone}</div>
      </div>
    </div>
  );

  if (d.type === "kalkulator_estimasi") {
    const kd = d.kalkulator_data || {};
    const rooms = (kd.rooms || []).filter((r) => roomTot(r).mid > 0);
    const lantais = kd.lantai || [];
    const ltHas = lantais.some((lt) => calcLantai(lt).mid > 0);
    const lt = lantaiTot(lantais);
    const costs = (kd.costs || []).filter((c) => costVal(c) > 0);
    const ct = costs.reduce((a, c) => a + costVal(c), 0);
    const totals = kd.totals || {};
    const secTh = { fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "#8A7B6A", padding: "5px 6px", borderBottom: "1px solid #E6DDD2", background: "#FAFAFA", textAlign: "left" };
    const secThR = { ...secTh, textAlign: "right" };
    const secTd = { padding: "4px 6px", borderBottom: "1px solid #F2EBE1", fontSize: 10, verticalAlign: "middle" };
    // nowrap + tabular-nums so a long rupiah figure never wraps onto a second line inside
    // its (fixed-width) column — that wrap was the "tumpang tindih" overlap the printed
    // table used to show once a number got past ~10 digits.
    const secTdR = { ...secTd, textAlign: "right", fontFamily: "monospace", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
    // Shrinks the font for a very long rupiah string instead of letting it wrap/overflow —
    // same idea as the Dashboard KPI cards' clamp(), just computed from the actual string
    // length since this renders identically on-screen and inside the print/PDF output.
    const bigFont = (str, max, min) => { const len = String(str).length; const px = len <= 14 ? max : Math.max(min, max - (len - 14) * 1.1); return px; };
    const Section = ({ title, sub, children }) => (
      <div style={{ marginBottom: 12, border: "1px solid #E6DDD2", borderRadius: 6, overflow: "hidden", breakInside: "avoid" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 10px", background: "#F2EBE1", borderBottom: "1px solid #E6DDD2", borderLeft: "3px solid #B8BDC8" }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, minWidth: 0 }}>{title}</span>
          <span style={{ fontSize: 9.5, fontFamily: "monospace", color: "#6E7480", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{sub}</span>
        </div>
        {children}
      </div>
    );

    // Opsi 2 — dokumen turunan dari Opsi 1 yang menghitung SEMUA kelas per uraian sekaligus
    // (lihat documentClassBreakdown di kalkulatorCatalog.js). Render sepenuhnya berbeda: per
    // item ditampilkan daftar harga di setiap kelas yang dipunyainya (bukan satu kelas
    // terpilih), lalu ditutup dengan daftar "Pilihan Paket" (satu baris per kelas, sudah
    // dijumlah se-dokumen) supaya client bisa membandingkan langsung.
    if (totals.opsi === 2) {
      const packages = totals.packages || [];
      return (
        <div className="sheet bg-white text-zinc-900 max-w-[794px] mx-auto" data-testid="doc-sheet" style={{ fontFamily: "Inter, Arial, sans-serif" }}>
          <div style={{ padding: "26px 36px" }}>
            <div style={{ paddingBottom: 12, borderBottom: "2px solid #B8BDC8", marginBottom: 14 }}>
              <Header compact />
              <div style={{ fontSize: 12, fontWeight: 700, color: "#8E95A3", marginTop: 2 }}>Kalkulator Estimasi Biaya Produksi Interior</div>
              <div style={{ fontSize: 9.5, fontWeight: 700, color: "#B8BDC8", marginTop: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>Opsi 2 — Semua Kelas, Silakan Pilih Paket</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 14, padding: "8px 10px", background: "#F8F4EF", border: "1px solid #E6DDD2", borderRadius: 6 }}>
              <div><div style={{ fontSize: 8, textTransform: "uppercase", color: "#8A7B6A", fontWeight: 700 }}>Nama Klien</div><div style={{ fontSize: 11 }}>{d.client?.nama || "—"}</div></div>
              <div><div style={{ fontSize: 8, textTransform: "uppercase", color: "#8A7B6A", fontWeight: 700 }}>No. HP / WA</div><div style={{ fontSize: 11 }}>{d.client?.telp || "—"}</div></div>
              <div><div style={{ fontSize: 8, textTransform: "uppercase", color: "#8A7B6A", fontWeight: 700 }}>Alamat Proyek</div><div style={{ fontSize: 11 }}>{d.client?.alamat || "—"}</div></div>
            </div>

            {(kd.rooms || []).map((room, ri) => {
              const itemsWithClasses = (room.items || []).map((it) => ({ it, all: calcItemAllClasses(it) })).filter(({ it, all }) => all?.length || calcItem(it).mid > 0);
              if (!itemsWithClasses.length) return null;
              return (
                <Section key={ri} title={`${ri + 1}. ${room.name || "Ruangan"}`}>
                  <div style={{ padding: "6px 10px" }}>
                    {itemsWithClasses.map(({ it, all }, ii) => {
                      const cat = CAT[it.cat];
                      const nm = cat?.label || it.name || "Pekerjaan";
                      const c = calcItem(it);
                      let volStr = "—";
                      if (it.sat === "m²") volStr = c.vol > 0 ? c.vol.toFixed(2) + " m²" : "—";
                      else if (it.sat === "m'") volStr = c.vol > 0 ? c.vol.toFixed(2) + " m'" : "—";
                      else if (it.sat === "Unit") volStr = (it.qty || 1) + " unit";
                      else volStr = "LS";
                      return (
                        <div key={ii} style={{ padding: "5px 0", borderBottom: ii < itemsWithClasses.length - 1 ? "1px solid #F2EBE1" : "none" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 10, fontWeight: 600 }}>
                            <span>{nm}</span>
                            <span style={{ color: "#8A7B6A", fontFamily: "monospace", whiteSpace: "nowrap" }}>{volStr}</span>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 3 }}>
                            {all ? all.map((g) => (
                              <span key={g.k} style={{ fontSize: 8.5, padding: "2px 6px", borderRadius: 5, whiteSpace: "nowrap", color: (PRINT_BADGE[g.b] || PRINT_BADGE.oth).text, background: (PRINT_BADGE[g.b] || PRINT_BADGE.oth).bg }}>
                                {g.k}: {rupiah(g.mid)}
                              </span>
                            )) : (
                              <span style={{ fontSize: 8.5, padding: "2px 6px", borderRadius: 5, color: "#6E7480", background: "#F2EBE1" }}>Biaya tetap: {rupiah(c.mid)}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Section>
              );
            })}

            {ltHas && (
              <Section title="Lantai · Top Table · Backsplash" sub={lt.mn === lt.mx ? rupiah(lt.mid) : `${rupiah(lt.mn)} – ${rupiah(lt.mx)}`}>
                <div style={{ padding: "6px 10px", fontSize: 9.5, color: "#6E7480" }}>Biaya tetap — sama di setiap paket, tidak tergantung kelas.</div>
              </Section>
            )}
            {costs.length > 0 && (
              <Section title="Biaya Tambahan & Opsional" sub={rupiah(ct)}>
                <div style={{ padding: "6px 10px", fontSize: 9.5, color: "#6E7480" }}>Biaya tetap — sama di setiap paket, tidak tergantung kelas.</div>
              </Section>
            )}

            <div style={{ marginTop: 16, padding: "16px 18px", background: "linear-gradient(155deg, #FAFAFA 0%, #F0F0F2 55%, #E4E4E7 100%)", border: "1px solid #E4E4E7", borderRadius: 10, color: "#18181B" }}>
              <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: "#71717A", fontWeight: 600, marginBottom: 8 }}>Total Estimasi per Paket</div>
              {packages.map((p, i) => (
                <div key={p.k} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, padding: "8px 0", borderTop: i > 0 ? "1px solid #D4D4D8" : "none" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700, color: "#18181B" }}>
                    <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: 99, background: (PRINT_BADGE[p.b] || PRINT_BADGE.oth).text }} />
                    {p.k}
                  </span>
                  {/* Fixed size (not bigFont's length-based shrink) — this box is wide
                      enough that no realistic total needs to shrink, and shrinking only
                      the longer figures made the four package totals look inconsistent
                      side by side even though nothing was at risk of overflowing. */}
                  <span style={{ fontSize: 15, fontWeight: 600, color: "#18181B", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{rupiah(p.final)}</span>
                </div>
              ))}
              <div style={{ fontSize: 8.5, color: "#8A8A90", lineHeight: 1.6, marginTop: 10, borderTop: "1px solid #D4D4D8", paddingTop: 8 }}>
                Setiap paket dihitung dari uraian &amp; volume pekerjaan yang sama, hanya kelas materialnya yang berbeda. Estimasi awal berdasarkan Price List 2026. Harga final menyesuaikan model, kerumitan &amp; material pilihan. Untuk project di bawah Rp 100 juta dikenakan biaya ongkir Rp 1.000.000 (Jabodetabek, Palembang &amp; sekitarnya). Free Survey · Free Konsultasi · Free Instalasi.
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="sheet bg-white text-zinc-900 max-w-[794px] mx-auto" data-testid="doc-sheet" style={{ fontFamily: "Inter, Arial, sans-serif" }}>
        <div style={{ padding: "26px 36px" }}>
          <div style={{ paddingBottom: 12, borderBottom: "2px solid #B8BDC8", marginBottom: 14 }}>
            <Header compact />
            <div style={{ fontSize: 12, fontWeight: 700, color: "#8E95A3", marginTop: 2 }}>Kalkulator Estimasi Biaya Produksi Interior</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 14, padding: "8px 10px", background: "#F8F4EF", border: "1px solid #E6DDD2", borderRadius: 6 }}>
            <div><div style={{ fontSize: 8, textTransform: "uppercase", color: "#8A7B6A", fontWeight: 700 }}>Nama Klien</div><div style={{ fontSize: 11 }}>{d.client?.nama || "—"}</div></div>
            <div><div style={{ fontSize: 8, textTransform: "uppercase", color: "#8A7B6A", fontWeight: 700 }}>No. HP / WA</div><div style={{ fontSize: 11 }}>{d.client?.telp || "—"}</div></div>
            <div><div style={{ fontSize: 8, textTransform: "uppercase", color: "#8A7B6A", fontWeight: 700 }}>Alamat Proyek</div><div style={{ fontSize: 11 }}>{d.client?.alamat || "—"}</div></div>
          </div>

          {rooms.map((room, ri) => {
            const tot = roomTot(room);
            return (
              <Section key={ri} title={`${ri + 1}. ${room.name || "Ruangan"}`} sub={tot.mn === tot.mx ? rupiah(tot.mid) : `${rupiah(tot.mn)} – ${rupiah(tot.mx)}`}>
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                  <thead><tr>
                    <th style={{ ...secTh, width: "auto" }}>Uraian Pekerjaan</th><th style={{ ...secTh, width: 60 }}>Kelas</th>
                    <th style={{ ...secThR, width: 56 }}>Volume</th><th style={{ ...secThR, width: 100 }}>Estimasi</th>
                  </tr></thead>
                  <tbody>
                    {room.items.filter((it) => calcItem(it).mid > 0).map((it, ii) => {
                      const c = calcItem(it);
                      const isMek2 = it.mode === 2;
                      const cat = CAT[it.cat];
                      const isMan = !isMek2 && (it.cat === "manual" || !cat || !cat.g);
                      const nm = isMek2 ? (it.name || "Area") : isMan ? (it.name || "Pekerjaan Manual") : (cat?.label || "");
                      const kelas = isMek2 ? (it.ki >= 0 ? MEK2_TIERS[it.ki] : null) : (!isMan && it.ki >= 0 && cat?.g) ? cat.g[it.ki] : null;
                      let volStr = "—";
                      if (it.sat === "m²") volStr = c.vol > 0 ? c.vol.toFixed(2) + " m²" : "—";
                      else if (it.sat === "m'") volStr = c.vol > 0 ? c.vol.toFixed(2) + " m'" : "—";
                      else if (it.sat === "Unit") volStr = (it.qty || 1) + " unit";
                      else volStr = "LS";
                      return (
                        <tr key={ii}>
                          <td style={secTd}>{nm}</td>
                          <td style={secTd}>{kelas ? <span style={{ fontSize: 8.5, fontWeight: 700, padding: "1px 5px", borderRadius: 6, color: (PRINT_BADGE[kelas.b] || PRINT_BADGE.oth).text, background: (PRINT_BADGE[kelas.b] || PRINT_BADGE.oth).bg }}>{kelas.k}</span> : ""}</td>
                          <td style={secTdR}>{volStr}</td>
                          <td style={{ ...secTdR, fontWeight: 700, fontSize: bigFont(rupiah(c.mid), 10, 7) }}>{rupiah(c.mid)}</td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td colSpan={3} style={{ ...secTd, background: "#F8F4EF", fontWeight: 700, borderTop: "1px solid #E6DDD2" }}>Subtotal {room.name || "Ruangan"}</td>
                      <td style={{ ...secTdR, background: "#F8F4EF", fontWeight: 700, borderTop: "1px solid #E6DDD2", fontSize: bigFont(rupiah(tot.mid), 10, 7) }}>{rupiah(tot.mid)}</td>
                    </tr>
                  </tbody>
                </table>
              </Section>
            );
          })}

          {ltHas && (
            <Section title="Lantai · Top Table · Backsplash" sub={lt.mn === lt.mx ? rupiah(lt.mid) : `${rupiah(lt.mn)} – ${rupiah(lt.mx)}`}>
              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <thead><tr><th style={{ ...secTh, width: "auto" }}>Material / Area</th><th style={{ ...secThR, width: 56 }}>Volume</th><th style={{ ...secThR, width: 108 }}>Estimasi</th></tr></thead>
                <tbody>
                  {lantais.filter((lti) => calcLantai(lti).mid > 0).map((lti, i) => {
                    const c = calcLantai(lti);
                    const mat = LANTAI_CAT[lti.mat];
                    const nm = (mat && lti.mat !== "" && lti.mat !== "lantai_manual") ? mat.label : "Material Manual";
                    let volStr = "—";
                    if (lti.sat === "m²") volStr = c.vol > 0 ? c.vol.toFixed(2) + " m²" : "—";
                    else if (lti.sat === "m'") volStr = c.vol > 0 ? c.vol.toFixed(2) + " m'" : "—";
                    else if (lti.sat === "Unit") volStr = (lti.qty || 1) + " unit";
                    else volStr = "LS";
                    return (
                      <tr key={i}>
                        <td style={secTd}>{nm}{lti.desc ? ` — ${lti.desc}` : ""}</td>
                        <td style={secTdR}>{volStr}</td>
                        <td style={{ ...secTdR, fontWeight: 700, fontSize: bigFont(rupiah(c.mid), 10, 7) }}>{rupiah(c.mid)}</td>
                      </tr>
                    );
                  })}
                  <tr><td colSpan={2} style={{ ...secTd, background: "#F8F4EF", fontWeight: 700, borderTop: "1px solid #E6DDD2" }}>Subtotal</td><td style={{ ...secTdR, background: "#F8F4EF", fontWeight: 700, borderTop: "1px solid #E6DDD2", fontSize: bigFont(rupiah(lt.mid), 10, 7) }}>{rupiah(lt.mid)}</td></tr>
                </tbody>
              </table>
            </Section>
          )}

          {costs.length > 0 && (
            <Section title="Biaya Tambahan & Opsional" sub={rupiah(ct)}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  {costs.map((c, i) => <tr key={i}><td style={secTd}>{c.name || "Biaya Lainnya"} — {c.qty} {c.sat}</td><td style={{ ...secTdR, fontWeight: 700 }}>{rupiah(costVal(c))}</td></tr>)}
                </tbody>
              </table>
            </Section>
          )}

          {/* Dark box with white text used to go invisible in print: Chrome doesn't print
              background-color by default (needs the user to manually tick "Background
              graphics"), so the box turned white with white-on-white text. Light card +
              dark text never depends on that toggle — legible whether backgrounds print or
              not, same reasoning as the rest of this document's tables. Palette matches the
              builder dialog's own light-mode sidebar gradient (usePalette() in
              KalkulatorBuilder.jsx) so the on-screen tool and the printed sheet read as one
              coherent white/gray design instead of the builder's silver next to a heavy
              near-black print box. */}
          <div style={{ marginTop: 16, padding: "16px 18px", background: "linear-gradient(155deg, #FAFAFA 0%, #F0F0F2 55%, #E4E4E7 100%)", border: "1px solid #E4E4E7", borderRadius: 10, color: "#18181B" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 10, color: "#71717A", marginBottom: 4 }}>
              <span>Total Sebelum Diskon</span><span style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{rupiah(totals.mid || 0)}</span>
            </div>
            {(totals.discount_amount || 0) > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 10, color: "#71717A", marginBottom: 8 }}>
                <span>{kd.discount?.type === "percent" ? `Diskon ${kd.discount.value}%` : "Diskon"}</span><span style={{ color: "#B03030", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>- {rupiah(totals.discount_amount)}</span>
              </div>
            )}
            <div style={{ height: 1, background: "#D4D4D8", margin: "10px 0" }} />
            <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, color: "#71717A", fontWeight: 600 }}>Total Estimasi Keseluruhan</div>
            {/* Fixed size, same reasoning as the Opsi 2 package totals above — this is a
                single wide summary box, not a narrow table cell, so there's no overflow
                risk that justifies shrinking by digit count. */}
            <div style={{ fontSize: 19, fontWeight: 600, color: "#18181B", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", marginTop: 2 }}>{rupiah(totals.final ?? totals.mid ?? 0)}</div>
            {totals.min !== totals.max && <div style={{ fontSize: 9.5, color: "#71717A", marginTop: 4, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>Estimasi Min: {rupiah(totals.final_min ?? totals.min ?? 0)} — Estimasi Maks: {rupiah(totals.final_max ?? totals.max ?? 0)}</div>}
            <div style={{ fontSize: 8.5, color: "#8A8A90", lineHeight: 1.6, marginTop: 10, borderTop: "1px solid #D4D4D8", paddingTop: 8 }}>
              Estimasi awal berdasarkan Price List 2026. Harga final menyesuaikan model, kerumitan &amp; material pilihan. Untuk project di bawah Rp 100 juta dikenakan biaya ongkir Rp 1.000.000 (Jabodetabek, Palembang &amp; sekitarnya). Free Survey · Free Konsultasi · Free Instalasi.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (d.type === "dokumen_lain" || d.type?.startsWith("doctype_")) {
    const sections = d.sections || [];
    return (
      <div className="sheet bg-white text-zinc-900 max-w-[794px] mx-auto" data-testid="doc-sheet" style={{ fontFamily: "Inter, Arial, sans-serif" }}>
        <div style={{ padding: "30px 42px" }}>
          <Header compact />
          <h1 style={{ textAlign: "center", fontSize: 15, fontWeight: 800, letterSpacing: 0.5, margin: "18px 0 4px" }}>{d.title || "DOKUMEN"}</h1>
          <p style={{ textAlign: "center", fontSize: 11, margin: "0 0 18px" }}>Nomor : {d.number} &nbsp;·&nbsp; {fmtDate(d.date)}</p>

          {!!(d.client?.nama || d.client?.alamat || d.client?.telp) && (
            <div style={{ marginBottom: 16, fontSize: 11, lineHeight: 1.8 }}>
              <p style={{ ...p, fontWeight: 700, margin: "0 0 4px" }}>Kepada Yth.</p>
              <p style={{ ...p, margin: 0 }}>{d.client?.nama || "-"}</p>
              {d.client?.alamat && <p style={{ ...p, margin: 0 }}>{d.client.alamat}</p>}
              {d.client?.telp && <p style={{ ...p, margin: 0 }}>{d.client.telp}</p>}
            </div>
          )}

          {sections.map((s, i) => (
            <div key={s.id || i} style={{ marginBottom: 14 }}>
              {s.title && <p style={{ ...p, fontWeight: 700, margin: "0 0 6px" }}>{s.title}</p>}
              <p style={{ ...pq, whiteSpace: "pre-wrap" }}>{s.body}</p>
            </div>
          ))}
          {!sections.length && <p style={{ ...pq, color: "#999" }}>Belum ada isi.</p>}

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 40, paddingBottom: 24 }}>
            <div style={{ textAlign: "center", width: "45%" }}>
              {sigImg ? <img src={fileUrl(sigImg)} alt="ttd" style={{ height: 50, objectFit: "contain", margin: "0 auto" }} /> : <div style={{ height: 56 }} />}
              <p style={{ ...pq, fontWeight: 700, textDecoration: "underline", margin: 0 }}>{d.signer_name || " "}</p>
              <p style={{ fontSize: 10, color: "#555", margin: 0 }}>{d.signer_title || " "}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (d.type === "kontrak_produksi") {
    const { hari, tanggal, bulan, tahun } = idDateParts(d.date);
    const sections = d.sections || [];
    const termins = d.termins || [];
    const subtotalP = items.reduce((a, it) => a + (it.amount ?? (Number(it.volume || 0) * Number(it.price || 0))), 0);
    const totalP = d.total ?? subtotalP;
    // Pasal 3's item table/sentence must show the pre-tax/pre-discount value (what the
    // RAB itself adds up to) — totalP (d.total) already has tax/discount folded in via
    // the backend's _compute_doc_totals, which is right for the Grand Total figure but
    // was wrong when shown as the "JUMLAH TOTAL" row directly under raw item rows.
    const subtotalDisplayP = d.subtotal ?? subtotalP;
    const persiapanHari = d.masa_persiapan_hari || 0;
    const persiapanStart = d.duration_start_date || d.date;
    const pekerjaanFisikStart = persiapanHari > 0 ? addDaysIso(persiapanStart, persiapanHari) : persiapanStart;
    // Kontrak Produksi renders at a slightly larger scale than SPH/Kontrak Design —
    // scoped locally (not touching the shared p/pq) so this doesn't affect other
    // document types. Rupiah figures get tabular-nums + nowrap so large amounts never
    // wrap mid-number and collide with the row above/below.
    const pK = { ...p, fontSize: 12.5 };
    const pqK = { ...pq, fontSize: 11.5 };
    const cellK = { ...cell, fontSize: 11.5 };
    const thK = { ...th, fontSize: 10.5 };
    const rupCell = { ...cellK, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
    return (
      <div className="sheet bg-white text-zinc-900 max-w-[794px] mx-auto" data-testid="doc-sheet" style={{ fontFamily: "Inter, Arial, sans-serif", position: "relative" }}>
        <div style={{ padding: "30px 42px 56px" }}>
          <Header compact />
          <h1 style={{ textAlign: "center", fontSize: 15, fontWeight: 800, letterSpacing: 1, margin: "18px 0 4px" }}>SURAT PERJANJIAN KERJA</h1>
          <p style={{ textAlign: "center", fontSize: 11.5, fontWeight: 600, margin: "0 0 4px" }}>Pelaksanaan Produksi Interior</p>
          <p style={{ textAlign: "center", fontSize: 11, margin: "0 0 18px" }}>Nomor Kontrak : {d.number}</p>

          <p style={{ ...pK, marginBottom: 18 }}>
            Pada hari {hari}, Tanggal {tanggal} Bulan {bulan} Tahun {tahun}, yang bertanda tangan di bawah ini sepakat mengikatkan diri dalam
            Perjanjian Kerja Pelaksanaan Produksi Interior dengan ketentuan sebagai berikut:
          </p>

          <div style={{ marginBottom: 14 }}>
            <p style={{ ...pK, fontWeight: 700, margin: "0 0 6px" }}>PIHAK PERTAMA (KLIEN / PEMBERI KERJA)</p>
            <div style={{ fontSize: 12, lineHeight: 1.8, display: "grid", gridTemplateColumns: "110px 1fr", gap: "1px 8px" }}>
              <span>Nama</span><span>: {d.client?.nama || "-"}</span>
              <span>NIK</span><span>: {d.nik_klien || "-"}</span>
              <span>Alamat</span><span>: {d.client?.alamat || "-"}</span>
              <span>No. Telepon</span><span>: {d.client?.telp || "-"}</span>
            </div>
            <p style={{ ...pK, marginTop: 6 }}>Selanjutnya dalam Perjanjian ini disebut sebagai <b>PIHAK PERTAMA</b>.</p>
          </div>

          <div style={{ marginBottom: 20 }}>
            <p style={{ ...pK, fontWeight: 700, margin: "0 0 6px" }}>PIHAK KEDUA (KONTRAKTOR / PELAKSANA)</p>
            <div style={{ fontSize: 12, lineHeight: 1.8, display: "grid", gridTemplateColumns: "110px 1fr", gap: "1px 8px" }}>
              <span>Nama</span><span>: {d.signer_name || "-"}</span>
              <span>Jabatan</span><span>: {d.signer_title || "-"}</span>
              <span>Nama Perusahaan</span><span>: {companyName}</span>
              <span>Alamat</span><span>: {d.company?.address || "-"}</span>
            </div>
            <p style={{ ...pK, marginTop: 6 }}>Selanjutnya dalam Perjanjian ini disebut sebagai <b>PIHAK KEDUA</b>.</p>
          </div>

          {sections.map((s, i) => (
            <div key={s.id || i} style={{ marginBottom: 14 }}>
              <p style={{ ...pK, fontWeight: 700, margin: "0 0 6px" }}>PASAL {i + 1}: {s.title?.toUpperCase()}</p>

              {s.kind === "lingkup_produksi" ? (
                <>
                  {s.body && <p style={{ ...pqK, whiteSpace: "pre-wrap" }}>{s.body}</p>}
                  {(d.event_tanggal_pelaksanaan || d.event_tanggal_bongkar) && (
                    <p style={pqK}>
                      Khusus untuk proyek ini: <b>Tanggal Pelaksanaan {d.event_tanggal_pelaksanaan ? fmtDate(d.event_tanggal_pelaksanaan) : "-"}</b>
                      {d.event_tanggal_bongkar && <> dan <b>Tanggal Pembongkaran {fmtDate(d.event_tanggal_bongkar)}</b></>}, mengikuti jadwal venue/pihak ketiga terkait.
                    </p>
                  )}
                </>
              ) : s.kind === "nilai_ppn" ? (
                <>
                  {d.sertakan_rincian_rab !== false ? (
                    <>
                      <p style={pqK}>Nilai total pekerjaan sebagaimana tercantum dalam Rencana Anggaran Biaya (RAB) pada Lampiran B adalah sebesar <b style={{ whiteSpace: "nowrap" }}>{rupiah(subtotalDisplayP)}</b>, dengan rincian sebagai berikut:</p>
                      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10 }}>
                        <thead><tr><th style={thK}>URAIAN</th><th style={{ ...thK, width: 70 }}>VOLUME</th><th style={{ ...thK, width: 55 }}>SATUAN</th><th style={{ ...thK, width: 120 }}>BIAYA/SATUAN</th><th style={{ ...thK, width: 130 }}>JUMLAH</th></tr></thead>
                        <tbody>
                          {items.map((it, ii) => (
                            <tr key={ii}>
                              <td style={cellK}>{it.description}</td>
                              <td style={{ ...cellK, textAlign: "right" }}>{it.volume || ""}</td>
                              <td style={{ ...cellK, textAlign: "center" }}>{it.unit}</td>
                              <td style={rupCell}>{rupiah(it.price)}</td>
                              <td style={rupCell}>{rupiah(it.amount ?? (Number(it.volume || 0) * Number(it.price || 0)))}</td>
                            </tr>
                          ))}
                          <tr><td style={{ ...cellK, fontWeight: 700, background: "#f3f3f3" }} colSpan={4}>JUMLAH TOTAL</td><td style={{ ...rupCell, fontWeight: 700, background: "#f3f3f3" }}>{rupiah(subtotalDisplayP)}</td></tr>
                        </tbody>
                      </table>
                    </>
                  ) : (
                    <p style={pqK}>Nilai total pekerjaan adalah sebesar <b style={{ whiteSpace: "nowrap" }}>{rupiah(subtotalDisplayP)}</b>, dengan rincian uraian pekerjaan, volume, dan harga satuan tercantum lengkap dalam Rencana Anggaran Biaya (RAB) pada Lampiran B yang merupakan bagian tidak terpisahkan dari kontrak ini.</p>
                  )}
                  {s.body && <p style={{ ...pqK, whiteSpace: "pre-wrap", marginTop: 8 }}>{s.body}</p>}
                  {d.kena_ppn ? (
                    <>
                      <p style={pqK}>Nilai kontrak di atas dikenakan Pajak Pertambahan Nilai (PPN) sebesar 11%, dihitung dari nilai pekerjaan sebelum pajak.</p>
                      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10 }}>
                        <tbody>
                          <tr><td style={cellK}>Sub Total (sebelum PPN)</td><td style={rupCell}>{rupiah(subtotalDisplayP)}</td></tr>
                          <tr><td style={cellK}>PPN 11%</td><td style={rupCell}>{rupiah(d.tax || 0)}</td></tr>
                          <tr><td style={{ ...cellK, fontWeight: 700, background: "#f3f3f3" }}>Total Nilai Kontrak (termasuk PPN)</td><td style={{ ...rupCell, fontWeight: 700, background: "#f3f3f3" }}>{rupiah(totalP)}</td></tr>
                        </tbody>
                      </table>
                    </>
                  ) : (
                    <p style={pqK}>Nilai kontrak di atas tidak dikenakan Pajak Pertambahan Nilai (PPN) kecuali PIHAK PERTAMA memerlukan Faktur Pajak resmi, dalam hal mana PPN 11% akan ditambahkan di luar nilai kontrak.</p>
                  )}
                </>
              ) : s.kind === "biaya_termin" ? (
                <>
                  <p style={pqK}>Pembayaran dilakukan secara bertahap berdasarkan Nilai Kontrak sebagaimana ditetapkan dalam Pasal sebelumnya, dengan tahapan sebagai berikut:</p>
                  {termins.length > 0 && (
                    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10 }}>
                      <thead><tr><th style={{ ...thK, width: 40 }}>TERMIN</th><th style={thK}>TAHAPAN PEMBAYARAN</th><th style={{ ...thK, width: 60 }}>%</th><th style={{ ...thK, width: 130 }}>JUMLAH</th><th style={thK}>SYARAT PENCAIRAN</th></tr></thead>
                      <tbody>
                        {termins.map((t, ti) => (
                          <tr key={ti}>
                            <td style={{ ...cellK, textAlign: "center" }}>{["I", "II", "III", "IV", "V", "VI"][ti] || ti + 1}</td>
                            <td style={cellK}>{t.label}</td>
                            <td style={{ ...cellK, textAlign: "right", whiteSpace: "nowrap" }}>{t.pct}%</td>
                            <td style={rupCell}>{rupiah(t.amount ?? Math.round(totalP * (t.pct || 0) / 100))}</td>
                            <td style={{ ...cellK, fontSize: 10.5 }}>{t.note || "-"}</td>
                          </tr>
                        ))}
                        <tr><td style={{ ...cellK, fontWeight: 700, background: "#f3f3f3" }} colSpan={3}>JUMLAH TOTAL</td><td style={{ ...rupCell, fontWeight: 700, background: "#f3f3f3" }}>{rupiah(totalP)}</td><td style={{ ...cellK, background: "#f3f3f3" }}></td></tr>
                      </tbody>
                    </table>
                  )}
                  {banks.length > 0 && (
                    <p style={pqK}>
                      REKENING PEMBAYARAN: {banks.map((b, bi) => (
                        <span key={bi}>{bi > 0 && "; "}<b>{b.acc_name}</b>, Bank {b.bank}, No. Rek {b.acc_no}</span>
                      ))}. Pembayaran hanya diterima melalui transfer bank ke rekening resmi PIHAK KEDUA di atas.
                    </p>
                  )}
                  <p style={pqK}>
                    <b>Keterlambatan Pembayaran:</b> toleransi {d.toleransi_hari ?? 3} hari kalender sejak jatuh tempo. Lewat batas toleransi tersebut
                    dikenakan denda {d.denda_pct_per_hari ?? 0.1}% per hari dari nilai termin yang tertunggak, dengan denda maksimum {d.denda_maks_pct ?? 5}%
                    dari nilai termin yang tertunggak. PIHAK KEDUA berhak menolak melanjutkan pekerjaan apabila termin yang jatuh tempo belum dibayar.
                  </p>
                </>
              ) : s.kind === "jangka_waktu" ? (
                <>
                  {d.duration_days ? (
                    <p style={pqK}>
                      Jangka waktu pelaksanaan pekerjaan fisik ditetapkan selama <b>{d.duration_days} hari kerja</b>, terhitung sejak tanggal <b>{fmtDurationRange(pekerjaanFisikStart, d.duration_days)}</b>
                      {persiapanHari > 0 && <> (setelah Masa Persiapan {persiapanHari} hari kerja sebagaimana diatur dalam Pasal berikutnya)</>}.
                    </p>
                  ) : (
                    <p style={pqK}>Jangka waktu pelaksanaan pekerjaan fisik ditetapkan selama <b>{d.duration_text || "-"}</b>.</p>
                  )}
                  {s.body && <p style={{ ...pqK, whiteSpace: "pre-wrap", marginTop: 8 }}>{s.body}</p>}
                </>
              ) : s.kind === "masa_persiapan" ? (
                <>
                  {persiapanHari > 0 ? (
                    <p style={pqK}>Masa Persiapan ditetapkan selama <b>{persiapanHari} hari kerja</b>, terhitung sejak tanggal <b>{fmtDurationRange(persiapanStart, persiapanHari)}</b>, sebelum pekerjaan fisik dimulai.</p>
                  ) : (
                    <p style={pqK}>Proyek ini tidak memiliki Masa Persiapan terpisah. Pekerjaan fisik dapat dimulai langsung sesuai Pasal Jangka Waktu Pelaksanaan.</p>
                  )}
                  {s.body && <p style={{ ...pqK, whiteSpace: "pre-wrap", marginTop: 8 }}>{s.body}</p>}
                </>
              ) : s.kind === "garansi" ? (
                <>
                  {s.body && <p style={{ ...pqK, whiteSpace: "pre-wrap" }}>{s.body}</p>}
                  <p style={{ ...pqK, marginTop: 8 }}>
                    <b>Masa Garansi:</b> PIHAK KEDUA memberikan garansi purna-produksi selama <b>{d.garansi_hari ?? 30} hari kalender</b> sejak BAST
                    ditandatangani, khusus untuk cacat pengerjaan (workmanship defect) dalam kondisi pemakaian, transportasi, dan penyimpanan yang wajar.
                    Garansi TIDAK berlaku untuk kerusakan akibat kelalaian pihak ketiga, force majeure, benturan/kecelakaan, atau modifikasi yang dilakukan
                    tanpa sepengetahuan PIHAK KEDUA. Klaim garansi wajib diajukan tertulis maksimal 3 hari sejak kerusakan ditemukan.
                  </p>
                </>
              ) : (
                <p style={{ ...pqK, whiteSpace: "pre-wrap" }}>{s.body}</p>
              )}
            </div>
          ))}

          {(d.head_designer || d.project_manager) && (
            <div style={{ marginBottom: 20 }}>
              <p style={{ ...pK, fontWeight: 700, margin: "0 0 6px" }}>DISAKSIKAN OLEH / PENANGGUNG JAWAB PROYEK</p>
              <div style={{ fontSize: 12, lineHeight: 1.8, display: "grid", gridTemplateColumns: "140px 1fr", gap: "1px 8px" }}>
                {d.head_designer && <><span>Head Designer</span><span>: {d.head_designer}</span></>}
                {d.project_manager && <><span>Project Manager</span><span>: {d.project_manager}</span></>}
              </div>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 40, paddingBottom: 24 }}>
            <div style={{ textAlign: "center", width: "45%" }}>
              <p style={{ ...pqK, marginBottom: 60 }}>PIHAK PERTAMA</p>
              <p style={{ ...pqK, borderTop: "1px solid #333", paddingTop: 4, margin: 0 }}>{d.client?.nama || " "}</p>
              <p style={{ fontSize: 10.5, color: "#555", margin: 0 }}>Klien {companyName}</p>
            </div>
            <div style={{ textAlign: "center", width: "45%" }}>
              <p style={{ ...pqK, marginBottom: 6 }}>PIHAK KEDUA</p>
              {sigImg ? <img src={fileUrl(sigImg)} alt="ttd" style={{ height: 50, objectFit: "contain", margin: "0 auto" }} /> : <div style={{ height: 56 }} />}
              <p style={{ ...pqK, fontWeight: 700, textDecoration: "underline", margin: 0 }}>{d.signer_name || " "}</p>
              <p style={{ fontSize: 10.5, color: "#555", margin: 0 }}>{d.signer_title || " "}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (d.type === "kontrak_design") {
    const { hari, tanggal, bulan, tahun } = idDateParts(d.date);
    const sections = d.sections || [];
    const termins = d.termins || [];
    const subtotalK = items.reduce((a, it) => a + (it.amount ?? (Number(it.volume || 0) * Number(it.price || 0))), 0);
    const totalK = d.total ?? subtotalK;
    return (
      <div className="sheet bg-white text-zinc-900 max-w-[794px] mx-auto" data-testid="doc-sheet" style={{ fontFamily: "Inter, Arial, sans-serif" }}>
        <div style={{ padding: "30px 42px" }}>
          <Header compact />
          <h1 style={{ textAlign: "center", fontSize: 15, fontWeight: 800, letterSpacing: 1, margin: "18px 0 4px" }}>SURAT PERJANJIAN PELAKSANAAN PEKERJAAN</h1>
          <p style={{ textAlign: "center", fontSize: 11, margin: "0 0 18px" }}>Nomor : {d.number}</p>

          <p style={{ ...p, marginBottom: 18 }}>
            Pada hari {hari}, Tanggal {tanggal} Bulan {bulan} Tahun {tahun}, kami yang bertanda tangan di bawah ini sepakat mengadakan
            Perjanjian Pelaksanaan Pekerjaan Jasa Desain Interior dengan ketentuan sebagai berikut:
          </p>

          <div style={{ marginBottom: 14 }}>
            <p style={{ ...p, fontWeight: 700, margin: "0 0 6px" }}>PIHAK PERTAMA (KLIEN)</p>
            <div style={{ fontSize: 11, lineHeight: 1.8, display: "grid", gridTemplateColumns: "110px 1fr", gap: "1px 8px" }}>
              <span>Nama</span><span>: {d.client?.nama || "-"}</span>
              <span>Alamat</span><span>: {d.client?.alamat || "-"}</span>
              <span>No. Telepon</span><span>: {d.client?.telp || "-"}</span>
            </div>
            <p style={{ ...p, marginTop: 6 }}>Selanjutnya dalam Perjanjian ini disebut sebagai <b>PIHAK PERTAMA</b>.</p>
          </div>

          <div style={{ marginBottom: 20 }}>
            <p style={{ ...p, fontWeight: 700, margin: "0 0 6px" }}>PIHAK KEDUA</p>
            <div style={{ fontSize: 11, lineHeight: 1.8, display: "grid", gridTemplateColumns: "110px 1fr", gap: "1px 8px" }}>
              <span>Nama</span><span>: {d.signer_name || "-"}</span>
              <span>Jabatan</span><span>: {d.signer_title || "-"}</span>
              <span>Nama Perusahaan</span><span>: {companyName}</span>
              <span>Alamat</span><span>: {d.company?.address || "-"}</span>
            </div>
            <p style={{ ...p, marginTop: 6 }}>Selanjutnya dalam Perjanjian ini disebut sebagai <b>PIHAK KEDUA</b>.</p>
          </div>

          {sections.map((s, i) => (
            <div key={s.id || i} style={{ marginBottom: 14 }}>
              <p style={{ ...p, fontWeight: 700, margin: "0 0 6px" }}>PASAL {i + 1}: {s.title?.toUpperCase()}</p>
              {s.kind === "jangka_waktu" ? (
                <>
                  {(() => {
                    const persiapanHariD = d.masa_persiapan_hari || 0;
                    const persiapanStartD = d.duration_start_date || d.date;
                    const pekerjaanStartD = persiapanHariD > 0 ? addDaysIso(persiapanStartD, persiapanHariD) : persiapanStartD;
                    return d.duration_days ? (
                      <p style={pq}>
                        Jangka waktu pelaksanaan pekerjaan ditetapkan selama <b>{d.duration_days} hari kerja</b>, terhitung sejak tanggal <b>{fmtDurationRange(pekerjaanStartD, d.duration_days)}</b>.
                        {persiapanHariD > 0 && <> Sebelum pekerjaan dimulai, terdapat Masa Persiapan (briefing, pengumpulan data, dan koordinasi awal) selama <b>{persiapanHariD} hari kerja</b>, terhitung sejak tanggal <b>{fmtDurationRange(persiapanStartD, persiapanHariD)}</b>.</>}
                      </p>
                    ) : (
                      <p style={pq}>Jangka waktu pelaksanaan pekerjaan ditetapkan selama <b>{d.duration_text || "-"}</b>, terhitung sejak diterimanya pembayaran Down Payment (DP) Termin I.</p>
                    );
                  })()}
                  {s.body && <p style={{ ...pq, whiteSpace: "pre-wrap", marginTop: 8 }}>{s.body}</p>}
                </>
              ) : s.kind === "biaya" ? (
                <>
                  <p style={pq}>Total biaya jasa desain adalah sebesar <b>{rupiah(totalK)}</b>.</p>
                  <p style={{ ...pq, fontWeight: 700, marginTop: 8 }}>Rincian biaya:</p>
                  <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10 }}>
                    <thead><tr><th style={th}>URAIAN</th><th style={{ ...th, width: 70 }}>VOLUME</th><th style={{ ...th, width: 45 }}>UNIT</th><th style={{ ...th, width: 100 }}>BIAYA/UNIT</th><th style={{ ...th, width: 110 }}>TOTAL</th></tr></thead>
                    <tbody>
                      {items.map((it, ii) => (
                        <tr key={ii}>
                          <td style={cell}>{it.description}</td>
                          <td style={{ ...cell, textAlign: "right" }}>{it.volume || ""}</td>
                          <td style={{ ...cell, textAlign: "center" }}>{it.unit}</td>
                          <td style={{ ...cell, textAlign: "right" }}>{rupiah(it.price)}</td>
                          <td style={{ ...cell, textAlign: "right" }}>{rupiah(it.amount ?? (Number(it.volume || 0) * Number(it.price || 0)))}</td>
                        </tr>
                      ))}
                      <tr><td style={{ ...cell, fontWeight: 700, background: "#f3f3f3" }} colSpan={4}>TOTAL</td><td style={{ ...cell, fontWeight: 700, textAlign: "right", background: "#f3f3f3" }}>{rupiah(totalK)}</td></tr>
                    </tbody>
                  </table>
                  {termins.length > 0 && (
                    <>
                      <p style={{ ...pq, fontWeight: 700 }}>Jadwal Pembayaran:</p>
                      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10 }}>
                        <thead><tr><th style={{ ...th, width: 40 }}>TERMIN</th><th style={th}>KONDISI PEMBAYARAN</th><th style={{ ...th, width: 70 }}>PERSENTASE</th><th style={{ ...th, width: 120 }}>JUMLAH (RP)</th></tr></thead>
                        <tbody>
                          {termins.map((t, ti) => (
                            <tr key={ti}>
                              <td style={{ ...cell, textAlign: "center" }}>{["I", "II", "III", "IV", "V", "VI"][ti] || ti + 1}</td>
                              <td style={cell}>{t.label}</td>
                              <td style={{ ...cell, textAlign: "right" }}>{t.pct}%</td>
                              <td style={{ ...cell, textAlign: "right" }}>{rupiah(t.amount ?? Math.round(totalK * (t.pct || 0) / 100))}</td>
                            </tr>
                          ))}
                          <tr><td style={{ ...cell, fontWeight: 700, background: "#f3f3f3" }} colSpan={3}>TOTAL</td><td style={{ ...cell, fontWeight: 700, textAlign: "right", background: "#f3f3f3" }}>{rupiah(totalK)}</td></tr>
                        </tbody>
                      </table>
                    </>
                  )}
                  {banks.length > 0 && (
                    <p style={pq}>
                      Transfer pembayaran melalui rekening: {banks.map((b, bi) => (
                        <span key={bi}>{bi > 0 && "; "}<b>{b.acc_name}</b>, Bank {b.bank}, No. Rek {b.acc_no}</span>
                      ))}
                    </p>
                  )}
                </>
              ) : (
                <p style={{ ...pq, whiteSpace: "pre-wrap" }}>{s.body}</p>
              )}
            </div>
          ))}

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 40, paddingBottom: 24 }}>
            <div style={{ textAlign: "center", width: "45%" }}>
              <p style={{ ...pq, marginBottom: 60 }}>PIHAK PERTAMA</p>
              <p style={{ ...pq, borderTop: "1px solid #333", paddingTop: 4, margin: 0 }}>{d.client?.nama || " "}</p>
              <p style={{ fontSize: 10, color: "#555", margin: 0 }}>Klien {companyName}</p>
            </div>
            <div style={{ textAlign: "center", width: "45%" }}>
              <p style={{ ...pq, marginBottom: 6 }}>PIHAK KEDUA</p>
              {sigImg ? <img src={fileUrl(sigImg)} alt="ttd" style={{ height: 50, objectFit: "contain", margin: "0 auto" }} /> : <div style={{ height: 56 }} />}
              <p style={{ ...pq, fontWeight: 700, textDecoration: "underline", margin: 0 }}>{d.signer_name || " "}</p>
              <p style={{ fontSize: 10, color: "#555", margin: 0 }}>{d.signer_title || " "}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return isQuo ? (
    <div className="sheet bg-white text-zinc-900 max-w-[794px] mx-auto" data-testid="doc-sheet" style={{ fontFamily: "Arial, sans-serif", border: "none" }}>
      <div className="page-break" style={{ padding: "26px 36px 16px" }}>
        <Header compact />
        <div style={{ fontSize: 10.5, marginTop: 22, display: "grid", gridTemplateColumns: "60px 1fr", gap: "3px 8px" }}>
          <span>Nomor</span><span>: {d.number}</span>
          <span>Tanggal</span><span>: {fmtDate(d.date)}</span>
          {tpl?.perihal && <><span>Perihal</span><span>: {tpl.perihal}</span></>}
        </div>
        <p style={{ ...pq, marginTop: 12 }}>Kepada Yth.</p>
        <p style={{ ...pq, fontWeight: 700 }}>{d.client?.nama}</p>
        <p style={pq}>Dengan hormat,</p>
        {tpl?.intro && <p style={{ ...pq, whiteSpace: "pre-wrap" }}>{tpl.intro}</p>}
        {/* Documents created before the flexible-sections feature have no `sections`
            field at all — they keep rendering off the old fixed ruang_lingkup/revisi
            template fields exactly as before, forever. Only documents that were
            actually created with sections (snapshotted at creation, see
            create_document in server.py) render this new dynamic list. */}
        {d.sections?.length ? (
          d.sections[0] && (
            <>
              <p style={{ ...pq, fontWeight: 700, marginTop: 10 }}>A. {d.sections[0].title} :</p>
              <p style={{ ...pq, whiteSpace: "pre-wrap" }}>{d.sections[0].body}</p>
            </>
          )
        ) : tpl?.ruang_lingkup && (
          <>
            <p style={{ ...pq, fontWeight: 700, marginTop: 10 }}>A. Ruang Lingkup Pekerjaan :</p>
            <p style={{ ...pq, whiteSpace: "pre-wrap" }}>{tpl.ruang_lingkup}</p>
          </>
        )}
      </div>

      <div style={{ padding: "26px 36px 0" }}>
        <Header compact />
        {d.sections?.length ? (
          d.sections.slice(1).map((s, i) => (
            <React.Fragment key={s.id || i}>
              <p style={{ ...pq, fontWeight: 700, marginTop: 18 }}>{s.title} :</p>
              <p style={{ ...pq, whiteSpace: "pre-wrap" }}>{s.body}</p>
            </React.Fragment>
          ))
        ) : tpl?.revisi && (
          <>
            <p style={{ ...pq, fontWeight: 700, marginTop: 18 }}>Revisi :</p>
            <p style={{ ...pq, whiteSpace: "pre-wrap" }}>{tpl.revisi}</p>
          </>
        )}

        <p style={{ ...pq, fontWeight: 700, marginTop: 10 }}>Jasa Perencanaan Desain :</p>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr><th style={{ ...th, width: 30 }}>NO</th><th style={{ ...th, textAlign: "left" }}>DESCRIPTION</th><th style={{ ...th, width: 55 }}>VOLUME</th><th style={{ ...th, width: 45 }}>UNIT</th><th style={{ ...th, width: 100 }}>PRICE</th><th style={{ ...th, width: 110 }}>AMOUNT</th></tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i}>
                <td style={{ ...cell, textAlign: "center" }}>{i + 1}</td>
                <td style={cell}>{it.description}</td>
                <td style={{ ...cell, textAlign: "right" }}>{it.volume || ""}</td>
                <td style={{ ...cell, textAlign: "center" }}>{it.unit}</td>
                <td style={{ ...cell, textAlign: "right" }}>{rupiah(it.price)}</td>
                <td style={{ ...cell, textAlign: "right" }}>{rupiah(it.amount)}</td>
              </tr>
            ))}
            <tr>
              <td style={{ ...cell, fontWeight: 700, textAlign: "center", background: "#f3f3f3" }} colSpan={2}>TOTAL</td>
              <td style={{ ...cell, fontWeight: 700, textAlign: "right", background: "#f3f3f3" }}>{totalVolume || ""}</td>
              <td style={{ ...cell, background: "#f3f3f3" }}></td>
              <td style={{ ...cell, background: "#f3f3f3" }}></td>
              <td style={{ ...cell, fontWeight: 700, textAlign: "right", background: "#f3f3f3" }}>{rupiah(d.subtotal ?? d.total)}</td>
            </tr>
          </tbody>
        </table>
        {/* Discount/tax used to be a floating line of text below the table, disconnected
            from any column — now a proper small table (same as the Invoice's summary
            block), right-aligned under the items table, auto-totaled from the same
            subtotal/discount_amount/tax/total the backend already computes. */}
        {(d.discount_amount > 0 || d.tax > 0) && (
          <table style={{ borderCollapse: "collapse", width: 260, marginTop: 6, marginLeft: "auto" }}>
            <tbody>
              <tr><td style={cell}>Sub Total</td><td style={{ ...cell, textAlign: "right" }}>{rupiah(d.subtotal)}</td></tr>
              {d.discount_amount > 0 && <tr><td style={cell}>Discount</td><td style={{ ...cell, textAlign: "right" }}>- {rupiah(d.discount_amount)}</td></tr>}
              {d.tax > 0 && <tr><td style={cell}>Tax</td><td style={{ ...cell, textAlign: "right" }}>{rupiah(d.tax)}</td></tr>}
              <tr><td style={{ ...cell, fontWeight: 800, background: "#f3f3f3" }}>Total</td><td style={{ ...cell, textAlign: "right", fontWeight: 800, background: "#f3f3f3" }}>{rupiah(d.total)}</td></tr>
            </tbody>
          </table>
        )}

        <p style={{ ...pq, fontWeight: 700, marginTop: 10 }}>Pembayaran desain fee dibayarkan secara :</p>
        <ol style={{ fontSize: 10.5, lineHeight: 1.4, paddingLeft: 18, margin: 0, listStyleType: "decimal", listStylePosition: "outside" }}>
          {(d.termins || []).length ? d.termins.map((t, i) => (
            <li key={i} style={{ marginBottom: 3 }}>{t.label || `Termin ${i + 1}`} dibayar PIHAK PERTAMA kepada PIHAK KEDUA sebesar {t.pct}% ({rupiah(t.amount)}) yang akan dibayarkan transfer sesuai kesepakatan.</li>
          )) : <li style={{ marginBottom: 3 }}>Pembayaran dilakukan sesuai kesepakatan kedua belah pihak.</li>}
          {banks.map((b, i) => (
            <li key={"b" + i} style={{ marginBottom: 3 }}>Pembayaran dikirim ke Bank {b.bank} No.{b.acc_no} atas {b.acc_name}</li>
          ))}
        </ol>

        {tpl?.closing && <p style={{ ...pq, marginTop: 10, whiteSpace: "pre-wrap" }}>{tpl.closing}</p>}

        <div style={{ marginTop: 18, paddingBottom: 24 }}>
          <p style={{ ...pq, margin: 0 }}>Hormat Kami,</p>
          <p style={{ ...pq, fontWeight: 700, margin: "0 0 6px" }}>{companyName} INTERIOR</p>
          {sigImg ? <img src={fileUrl(sigImg)} alt="ttd" style={{ height: 50, objectFit: "contain" }} /> : <div style={{ height: 50 }} />}
          <div style={{ fontWeight: 700, textDecoration: "underline", fontSize: 10.5 }}>{d.signer_name || " "}</div>
          <div style={{ fontSize: 10.5, color: "#444" }}>{sig.title || d.signer_title}</div>
        </div>
      </div>
    </div>
  ) : (
    <div className="sheet bg-white text-zinc-900 max-w-[794px] mx-auto" data-testid="doc-sheet" style={{ fontFamily: "Arial, sans-serif", border: "none" }}>
      <div style={{ padding: "40px 40px 0" }}>
        <Header />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "2px solid #1a1a1a", borderBottom: "2px solid #1a1a1a", padding: "8px 0", margin: "6px 0 16px" }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>INVOICE / FAKTUR</div>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{d.number}</div>
        </div>

        <div style={{ display: "flex", gap: 14, marginBottom: 16 }}>
          <div style={{ flex: 1, border: bd, borderRadius: 10, padding: 12, fontSize: 11 }}>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 8px" }}>
              <b>Invoice To</b><span>: {d.client?.nama}</span>
              <b>No. Telepon</b><span>: {d.client?.telp || "-"}</span>
              <b>Alamat</b><span>: {d.client?.alamat || "-"}</span>
            </div>
          </div>
          <div style={{ width: 260, fontSize: 11 }}>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "3px 8px" }}>
              <b>Invoice Date</b><span>: {fmtDate(d.date)}</span>
              {hasBranch ? (
                <>
                  <b>Alamat</b><span>: {branch.address || "-"}</span>
                  <b>Tel. No</b><span>: {branch.phone || "-"}</span>
                  <b>Email</b><span>: {branch.email || "-"}</span>
                </>
              ) : (
                <>
                  <b>Email</b><span>: {d.client?.email || "-"}</span>
                  <b>Status</b><span>: {displayStatus}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr><th style={{ ...th, width: 30 }}>NO</th><th style={{ ...th, textAlign: "left" }}>DESCRIPTION</th><th style={{ ...th, width: 55 }}>VOLUME</th><th style={{ ...th, width: 45 }}>UNIT</th><th style={{ ...th, width: 100 }}>PRICE</th><th style={{ ...th, width: 110 }}>AMOUNT</th></tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i}>
                <td style={{ ...cell, textAlign: "center" }}>{i + 1}</td>
                <td style={cell}>{it.description}</td>
                <td style={{ ...cell, textAlign: "right" }}>{it.volume || ""}</td>
                <td style={{ ...cell, textAlign: "center" }}>{it.unit}</td>
                <td style={{ ...cell, textAlign: "right" }}>{rupiah(it.price)}</td>
                <td style={{ ...cell, textAlign: "right" }}>{rupiah(it.amount)}</td>
              </tr>
            ))}
            {Array.from({ length: fillers }).map((_, i) => (
              <tr key={"f" + i}><td style={cell}>&nbsp;</td><td style={cell}></td><td style={cell}></td><td style={cell}></td><td style={cell}></td><td style={cell}></td></tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "space-between", gap: 20, marginTop: 16 }}>
          <table style={{ borderCollapse: "collapse", alignSelf: "flex-start", width: 300 }}>
            <tbody>
              <tr><td style={{ ...cell, fontWeight: 700 }} colSpan={2}>PAYMENT METHOD</td></tr>
              {banks.length ? banks.map((b, i) => (
                <React.Fragment key={i}>
                  {banks.length > 1 && <tr><td style={{ ...cell, fontWeight: 700, background: "#fafafa" }} colSpan={2}>{b.label}</td></tr>}
                  <tr><td style={cell}>ACCOUNT</td><td style={cell}>: {b.acc_name}</td></tr>
                  <tr><td style={cell}>BANK</td><td style={cell}>: {b.bank}</td></tr>
                  <tr><td style={cell}>ACC.NO</td><td style={cell}>: {b.acc_no}</td></tr>
                </React.Fragment>
              )) : (
                <>
                  <tr><td style={cell}>ACCOUNT</td><td style={cell}>: {d.payment?.account}</td></tr>
                  <tr><td style={cell}>BANK</td><td style={cell}>: {d.payment?.bank}</td></tr>
                  <tr><td style={cell}>ACC.NO</td><td style={cell}>: {d.payment?.acc_no}</td></tr>
                </>
              )}
            </tbody>
          </table>
          <table style={{ borderCollapse: "collapse", width: 300 }}>
            <tbody>
              <tr><td style={cell}>Sub Total</td><td style={{ ...cell, textAlign: "right" }}>{rupiah(d.subtotal)}</td></tr>
              <tr><td style={cell}>Discount</td><td style={{ ...cell, textAlign: "right" }}>{d.discount_amount ? "- " + rupiah(d.discount_amount) : "-"}</td></tr>
              <tr><td style={cell}>Tax</td><td style={{ ...cell, textAlign: "right" }}>{d.tax ? rupiah(d.tax) : "-"}</td></tr>
              <tr><td style={{ ...cell, fontWeight: 800, background: "#f3f3f3" }}>Total Tagihan</td><td style={{ ...cell, textAlign: "right", fontWeight: 800, background: "#f3f3f3" }}>{rupiah(d.total)}</td></tr>
              {(d.termins || []).map((t, i) => (
                <tr key={i}><td style={cell}>{t.label} {t.pct}%</td><td style={{ ...cell, textAlign: "right" }}>{rupiah(t.amount)}</td></tr>
              ))}
              <tr><td style={{ ...cell, color: "#c0392b", fontWeight: 700 }}>Sisa Tagihan</td><td style={{ ...cell, textAlign: "right", color: "#c0392b", fontWeight: 700 }}>{rupiah(sisaTagihan)}</td></tr>
              <tr><td style={{ ...cell, fontWeight: 800, background: "#f3f3f3" }}>Status</td><td style={{ ...cell, textAlign: "right", fontWeight: 800, background: "#f3f3f3" }}>{displayStatus}</td></tr>
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 70, paddingBottom: 30 }}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 26, fontWeight: 700, textDecoration: "underline", lineHeight: 1.2 }}>
            Thank you<br />for purchase!
          </div>
          <div style={{ textAlign: "center", minWidth: 200 }}>
            {sigImg ? <img src={fileUrl(sigImg)} alt="ttd" style={{ height: 60, objectFit: "contain" }} /> : <div style={{ height: 60 }} />}
            <div style={{ fontWeight: 700, textDecoration: "underline" }}>{d.signer_name || " "}</div>
            <div style={{ fontSize: 12, color: "#444" }}>{sig.title || d.signer_title}</div>
          </div>
        </div>
        {d.notes && <div style={{ fontSize: 10, color: "#666", marginTop: 12, paddingBottom: 20, whiteSpace: "pre-wrap" }}>{d.notes}</div>}
      </div>
    </div>
  );
}
